import { GroupedSalesRow, GroupedSalesTotals } from './group-sales';
import {
  LocationWithCategories,
  CategoryByLocationStat,
} from 'src/app/models/core/sales-manager/category-sales.model';

/** Fields shared by locations and categories that map to the six table columns. */
type MetricSource = Pick<
  LocationWithCategories,
  | 'total_tickets_sold'
  | 'quota_paid'
  | 'quota_pay_at_door'
  | 'total_assistants'
  | 'verified_assistants'
  | 'total_amount'
>;

const EMPTY_TOTALS: GroupedSalesTotals = {
  quota: 0,
  quota_paid: 0,
  quota_pay_at_door: 0,
  total_registered: 0,
  total_verified: 0,
  total: 0,
};

/**
 * Translates the backend field names into the six `GroupedSalesTotals` columns
 * the Grupo Venta rows render. Pure: builds a fresh object.
 */
function toMetrics(source: MetricSource): GroupedSalesTotals {
  return {
    quota: source.total_tickets_sold,
    quota_paid: source.quota_paid,
    quota_pay_at_door: source.quota_pay_at_door,
    total_registered: source.total_assistants,
    total_verified: source.verified_assistants,
    total: source.total_amount,
  };
}

/**
 * Maps the `categories-by-location` payload to the Grupo Venta view
 * model: one collapsible local per entry, each expanding into its categories.
 * Pure.
 */
export function categoriesByLocationToGroupedRows(
  locations: ReadonlyArray<LocationWithCategories>
): GroupedSalesRow[] {
  return locations.map((location) => ({
    id: String(location.location_id),
    label: location.location_name,
    totals: toMetrics(location),
    rows: location.categories.map((category: CategoryByLocationStat) => ({
      label: category.category_name,
      ...toMetrics(category),
    })),
  }));
}

/**
 * Grand totals row: sums the six columns across all locations. Pure — never
 * mutates the input. Returns zeros for an empty list.
 */
export function computeCategoriesTotals(
  locations: ReadonlyArray<LocationWithCategories>
): GroupedSalesTotals {
  return locations.reduce<GroupedSalesTotals>(
    (acc, location) => ({
      quota: acc.quota + location.total_tickets_sold,
      quota_paid: acc.quota_paid + location.quota_paid,
      quota_pay_at_door: acc.quota_pay_at_door + location.quota_pay_at_door,
      total_registered: acc.total_registered + location.total_assistants,
      total_verified: acc.total_verified + location.verified_assistants,
      total: acc.total + location.total_amount,
    }),
    { ...EMPTY_TOTALS }
  );
}
